import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminApi } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { fmtDate, fmtMoney, fmtNum, StatusPill, Panel, PageHeader, Th, Td, Empty } from "@/components/admin/parts";

const FILTERS = [
  { key: "all", label: "Wszystkie" },
  { key: "paid", label: "Opłacone" },
  { key: "checkout_created", label: "W trakcie" },
  { key: "failed", label: "Nieudane" },
  { key: "refunded", label: "Zwrócone" },
  { key: "disputed", label: "Spory" },
  { key: "amount_mismatch", label: "Do sprawdzenia" },
];

export default function Payments() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [events, setEvents] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => adminApi.payments({ status: filter, limit: 100 }).then(setData).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => { adminApi.webhookEvents({ status: "error", limit: 50 }).then(setEvents).catch(() => {}); }, []);

  const refund = async (p) => {
    if (!window.confirm(`Pełny zwrot zakupu ${p.pack_name} (${fmtMoney(p.gross_price, p.currency)})? Kredyty zostaną cofnięte po potwierdzeniu przez Stripe.`)) return;
    setBusy(p.id);
    try {
      await adminApi.refundPurchase(p.id);
      toast.success("Zwrot utworzony w Stripe. Kredyty cofnie webhook.");
      setTimeout(load, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie udało się utworzyć zwrotu.");
    } finally { setBusy(""); }
  };

  const retry = async (id) => {
    setBusy(id);
    try {
      await adminApi.retryWebhook(id);
      toast.success("Zdarzenie ponowione.");
      adminApi.webhookEvents({ status: "error", limit: 50 }).then(setEvents);
    } catch { toast.error("Ponowienie nie powiodło się."); }
    finally { setBusy(""); }
  };

  return (
    <div>
      <PageHeader
        title="Płatności"
        subtitle="Zakupy kredytów, zwroty, spory i zdarzenia Stripe"
        action={<span className="rounded-full border border-brand-line px-3 py-1.5 text-xs text-brand-sec">Zdarzenia webhook: {fmtNum(data?.webhookEvents)}</span>}
      />

      {events?.items?.length > 0 && (
        <div className="mb-5"><Panel>
          <p className="mb-2 text-sm font-semibold text-red-400">Błędne zdarzenia webhook ({events.items.length})</p>
          <div className="space-y-2" data-testid="admin-webhook-errors">
            {events.items.map((e) => (
              <div key={e.eventId} className="flex flex-wrap items-center justify-between gap-2 rounded-field border border-red-500/30 bg-red-500/5 px-3 py-2 text-xs">
                <span className="text-brand-sec">{e.type} · {e.eventId} · prób: {e.attempts} · {e.error}</span>
                <button onClick={() => retry(e.eventId)} disabled={busy === e.eventId} data-testid={`admin-webhook-retry-${e.eventId}`} className="rounded-button border border-brand-line px-3 py-1.5 font-semibold text-brand-text hover:border-brand-accent/60 disabled:opacity-50">Ponów bezpiecznie</button>
              </div>
            ))}
          </div>
        </Panel></div>
      )}

      <div className="mb-4 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)} className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${filter === f.key ? "bg-brand-accent text-brand-on" : "border border-brand-line text-brand-sec hover:text-brand-text"}`}>{f.label}</button>
        ))}
      </div>
      <Panel testId={ADMIN.paymentsTable}>
        {!data ? <Empty>Ładowanie…</Empty> : data.items.length === 0 ? <Empty>Brak płatności.</Empty> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px]">
              <thead><tr className="border-b border-brand-line">
                <Th>Data</Th><Th>Użytkownik</Th><Th>Pakiet</Th><Th className="text-right">Kredyty</Th><Th className="text-right">Cofnięte</Th><Th className="text-right">Kwota</Th><Th>Status</Th><Th></Th>
              </tr></thead>
              <tbody>
                {data.items.map((p) => (
                  <tr key={p.id} className="border-b border-brand-line/60" data-testid={`admin-payment-${p.id}`}>
                    <Td className="whitespace-nowrap text-brand-sec">{fmtDate(p.paid_at || p.created_at)}</Td>
                    <Td className="text-brand-sec">{p.user_email}</Td>
                    <Td>{p.pack_name}</Td>
                    <Td className="text-right font-semibold text-brand-accent">{fmtNum(p.credits_total)}</Td>
                    <Td className="text-right text-brand-sec">{p.credits_reversed ? `-${fmtNum(p.credits_reversed)}` : "—"}</Td>
                    <Td className="text-right">{fmtMoney(p.gross_price, p.currency)}</Td>
                    <Td><StatusPill status={p.status} label={p.status === "paid" ? "OPŁACONE" : p.status} /></Td>
                    <Td className="text-right">
                      {p.status === "paid" && (
                        <button onClick={() => refund(p)} disabled={busy === p.id} data-testid={`admin-refund-${p.id}`} className="rounded-button border border-brand-line px-3 py-1.5 text-xs font-semibold text-brand-text hover:border-red-500/60 disabled:opacity-50">Pełny zwrot</button>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
